import React, { useCallback, useMemo } from 'react';
import { Form } from 'antd';
import type { FormItemProps } from 'antd';
import PhoneInput from 'antd-phone-input';
import { useTranslation } from 'react-i18next';
import { isE164Phone, toE164Phone, type AntdPhoneInputValue } from '@/utils/phoneValidation';

interface PhoneFormItemProps extends Omit<FormItemProps, 'children'> {
  /** antd Input size. Default "middle". */
  size?: 'small' | 'middle' | 'large';
  enableSearch?: boolean;
  /** Overrides the default invalid-phone copy. */
  invalidMessage?: string;
}

function hasPhoneInputContent(value: unknown): boolean {
  if (value == null || value === '') return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value !== 'object') return false;
  const phone = value as AntdPhoneInputValue;
  return (
    phone.countryCode != null ||
    (phone.areaCode != null && String(phone.areaCode).length > 0) ||
    (phone.phoneNumber != null && String(phone.phoneNumber).length > 0)
  );
}

/**
 * Reusable Ant Design Form.Item wrapping antd-phone-input.
 *
 * The form field value stays as the library's PhoneNumber object (antd-phone-input
 * writes that object into the Form directly). Convert to E.164 with {@link toE164Phone}
 * only when calling APIs.
 */
export const PhoneFormItem: React.FC<PhoneFormItemProps> = ({
  size = 'middle',
  enableSearch = true,
  invalidMessage,
  rules,
  validateTrigger,
  ...formItemProps
}) => {
  const { t, i18n } = useTranslation('manage');

  const defaultCountry = useMemo(() => {
    const lang = i18n.language.toLowerCase();
    const parts = lang.split('-');
    if (parts.length > 1) return parts[1];
    const langToCountry: Record<string, string> = {
      en: 'us',
      ja: 'jp',
      ko: 'kr',
      zh: 'cn',
      da: 'dk',
      sv: 'se',
      cs: 'cz',
      el: 'gr',
      he: 'il',
      ar: 'sa',
      uk: 'ua',
      bg: 'bg'
    };
    return langToCountry[parts[0]] ?? parts[0];
  }, [i18n.language]);

  const defaultInvalidMessage =
    invalidMessage ?? t('appointmentComponents.create.guest.phoneInvalid');

  const phoneValidator = useCallback(
    (_: unknown, value: unknown) => {
      // Empty: let the `required` rule handle it; don't show format errors while empty.
      if (!hasPhoneInputContent(value)) {
        return Promise.resolve();
      }

      if (typeof value === 'object' && value !== null) {
        const phone = value as AntdPhoneInputValue;
        if (typeof phone.valid === 'function' && phone.valid()) {
          return Promise.resolve();
        }
      }

      const e164 = toE164Phone(value);
      if (isE164Phone(e164)) {
        return Promise.resolve();
      }

      // Incomplete entries (e.g. "+359 (323)") fail library validity until the full number is typed.
      return Promise.reject(new Error(defaultInvalidMessage));
    },
    [defaultInvalidMessage]
  );

  const mergedRules = rules
    ? [...rules, { validator: phoneValidator }]
    : [{ validator: phoneValidator }];

  return (
    <Form.Item
      {...formItemProps}
      rules={mergedRules}
      // Avoid flashing errors mid-keystroke while the national number is incomplete.
      validateTrigger={validateTrigger ?? ['onBlur', 'onSubmit']}
    >
      <PhoneInput enableSearch={enableSearch} size={size} country={defaultCountry} />
    </Form.Item>
  );
};
